/*
 * Bioroot product page add-ons (main-product + featured-product), on top of Dawn's
 * product-info.js / product-form.js / media-gallery.js. Replaces the Shrine behaviours used by the
 * configured blocks: sticky add-to-cart, delivery date, sale countdown, product videos, simple upsell,
 * gallery pagination dots, Kaching / Loop price syncs and variant-change swaps of Shrine-only markup.
 */
(() => {
  if (window.BrProduct) return;
  window.BrProduct = true;

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  const onReady = (fn) =>
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn();

  /* ---------- Variant change: swap Shrine-only fragments from the re-rendered section ---------- */
  const swapFromHtml = (sectionId, html) => {
    const root = document.querySelector(`product-info[data-section="${sectionId}"]`) || document;
    root.querySelectorAll('[data-br-swap][id]').forEach((el) => {
      const fresh = html.getElementById(el.id);
      if (!fresh) return;
      el.innerHTML = fresh.innerHTML;
      if (el.hasAttribute('data-br-swap-disabled')) el.toggleAttribute('disabled', fresh.hasAttribute('disabled'));
    });

    // Quantity breaks: prices depend on the selected variant; keep the selected option.
    const breaks = document.getElementById(`quantity-breaks-${sectionId}`);
    const freshBreaks = html.getElementById(`quantity-breaks-${sectionId}`);
    if (breaks && freshBreaks) breaks.refresh?.(freshBreaks);
  };

  /* Sticky add-to-cart buttons submit the main form through the form attribute: mirror its loading state. */
  const stopStickyLoading = () =>
    document.querySelectorAll('.sticky-atc__atc.loading').forEach((button) => {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    });
  document.addEventListener('submit', (event) => {
    const button = event.submitter;
    if (!button?.classList.contains('sticky-atc__atc')) return;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    setTimeout(stopStickyLoading, 8000);
  });

  const initPubSub = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data?.html) swapFromHtml(data.sectionId, data.html);
    });
    subscribe(PUB_SUB_EVENTS.cartUpdate, stopStickyLoading);
    subscribe(PUB_SUB_EVENTS.cartError, stopStickyLoading);
  };
  onReady(initPubSub);

  /* ---------- Gallery pagination dots (Dawn's slider-component has no dot support) ---------- */
  const initGalleryDots = (root = document) => {
    root.querySelectorAll('media-gallery slider-component[id^="GalleryViewer-"]').forEach((slider) => {
      if (slider.dataset.brDots) return;
      const dots = Array.from(slider.querySelectorAll('.slider-counter__link--dots'));
      if (!dots.length) return;
      slider.dataset.brDots = 'true';
      const list = slider.querySelector('.slider');
      const setActive = (index) =>
        dots.forEach((dot, i) => {
          dot.classList.toggle('slider-counter__link--active', i === index);
          dot.toggleAttribute('aria-current', i === index);
        });
      const visibleSlides = () => Array.from(list.querySelectorAll('.slider__slide')).filter((s) => s.clientWidth > 0);
      slider.addEventListener('slideChanged', (e) => setActive((e.detail?.currentPage || 1) - 1));
      dots.forEach((dot, i) =>
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          const target = visibleSlides()[i];
          if (target) list.scrollTo({ left: target.offsetLeft - list.firstElementChild.offsetLeft });
        })
      );
    });
  };
  onReady(() => initGalleryDots());

  /* ---------- Delivery date ("ships by Month Dth") and sale countdown ("Ends in HH:MM:SS") ---------- */
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const ordinal = (d) => (d > 3 && d < 21 ? 'th' : ['th', 'st', 'nd', 'rd'][d % 10] || 'th');
  const initDates = (root = document) => {
    root.querySelectorAll('[data-br-delivery-days]').forEach((el) => {
      const date = new Date();
      date.setDate(date.getDate() + (parseInt(el.dataset.brDeliveryDays, 10) || 0));
      const target = el.querySelector('.js-delivery-date');
      if (target) target.textContent = `${MONTHS[date.getMonth()]} ${date.getDate()}${ordinal(date.getDate())}`;
    });
  };
  const pad = (n) => String(n).padStart(2, '0');
  const tickTimers = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
    const text = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
    document.querySelectorAll('[data-br-timer]').forEach((el) => {
      el.textContent = el.dataset.brTimer.replace('[timer]', text);
    });
  };
  onReady(() => {
    initDates();
    if (document.querySelector('[data-br-timer]')) {
      tickTimers();
      setInterval(tickTimers, 1000);
    }
  });

  /* ---------- Sticky add to cart ---------- */
  define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        const section = this.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${section}`);
        this.afterScroll = this.dataset.afterScroll === 'true' && !!this.mainButton;

        if (this.afterScroll) {
          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        } else {
          this.classList.add('sticky-atc--visible');
        }
        this.setBodySpacing();
        this.resizeObserver = new ResizeObserver(() => this.setBodySpacing());
        this.resizeObserver.observe(this);

        const scrollBtn = this.querySelector('.sticky-atc__scroll-btn');
        if (scrollBtn && this.dataset.scrollDestination) {
          scrollBtn.addEventListener('click', () => {
            const selector = this.dataset.scrollDestination.replace(/-id$/, `-${section}`);
            const destination = document.querySelector(selector);
            if (!destination) return;
            const header = document.querySelector('sticky-header');
            const offset = header ? header.clientHeight : 0;
            window.scrollTo({ top: destination.getBoundingClientRect().top + window.scrollY - offset - 15, behavior: 'smooth' });
          });
        }

        this.addEventListener('change', (e) => this.onPickerChange(e));
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        document.body.style.paddingBottom = '';
        this._init = false;
      }

      /* Same test as live (Shrine StickyAtc.checkATCScroll): offsetTop is relative to the button's
         offsetParent (the sticky info column on desktop), so the bar can show slightly before the main
         button has fully left the viewport. Kept for parity with live. */
      onScroll() {
        const button = this.mainButton;
        this.classList.toggle('sticky-atc--visible', window.scrollY > button.offsetTop + button.offsetHeight);
      }

      setBodySpacing() {
        document.body.style.paddingBottom = `${Math.max(this.offsetHeight - 1, 0)}px`;
      }

      /* Mirror the sticky picker into the main variant picker, which drives Dawn's product-info flow. */
      onPickerChange(event) {
        const select = event.target.closest('select');
        if (!select) return;
        const main = document.getElementById(`variant-selects-${this.dataset.section}`);
        if (!main) return;
        let values;
        if (select.hasAttribute('data-sticky-variant-select')) {
          values = (select.selectedOptions[0]?.dataset.options || '').split('||');
        } else {
          values = Array.from(this.querySelectorAll('[data-sticky-option]')).map((s) => s.value);
        }
        const groups = main.querySelectorAll('fieldset, .product-form__input--dropdown');
        let changed = null;
        groups.forEach((group, i) => {
          const value = values[i];
          if (value === undefined) return;
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
          if (radio && !radio.checked) {
            radio.checked = true;
            changed = radio;
          }
          const dropdown = group.querySelector('select');
          if (dropdown && dropdown.value !== value) {
            dropdown.value = value;
            changed = dropdown;
          }
        });
        changed?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );

  /* ---------- Product videos: tap to play/pause, one at a time, scroll progress ---------- */
  define(
    'product-videos',
    class ProductVideos extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        this.videos = Array.from(this.querySelectorAll('video'));
        this.track = this.querySelector('.product-videos__track');
        this.bar = this.querySelector('.product-videos__progress span');
        this.videos.forEach((video) => {
          const wrapper = video.closest('.video-wrapper');
          wrapper.querySelector('.product-videos__toggle')?.addEventListener('click', () => {
            if (video.paused) {
              this.videos.forEach((other) => other !== video && other.pause());
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
          video.addEventListener('play', () => wrapper.classList.add('is-playing'));
          video.addEventListener('pause', () => wrapper.classList.remove('is-playing'));
          video.addEventListener('ended', () => wrapper.classList.remove('is-playing'));
        });
        if (this.track && this.bar) {
          const update = () => {
            const max = this.track.scrollWidth - this.track.clientWidth;
            const visible = this.track.clientWidth / Math.max(this.track.scrollWidth, 1);
            const progress = max > 0 ? this.track.scrollLeft / max : 1;
            this.bar.style.width = `${Math.min(100, (visible + (1 - visible) * progress) * 100)}%`;
          };
          this.track.addEventListener('scroll', update, { passive: true });
          window.addEventListener('resize', update);
          update();
        }
      }
    }
  );

  /* ---------- Simple product upsell: add one unit through Dawn's cart drawer contract ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.product-upsell-button[data-upsell-variant]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items: [{ id: Number(button.dataset.upsellVariant), quantity: 1 }] };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(button);
    }
    button.classList.add('is-loading');
    button.disabled = true;
    fetch(`${window.routes.cart_add_url}.js`, { ...fetchConfig('javascript'), body: JSON.stringify(body) })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message);
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: button.dataset.upsellVariant, cartData: response });
        cart.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
        window.alert('There was an error adding the product to your cart. Please try again.');
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.disabled = false;
      });
  });

  /* ---------- Kaching Bundles -> "Price with Savings" block (live inline script) ---------- */
  const syncKachingPrice = () => {
    const selected = document.querySelector('.kaching-bundles__bar--selected');
    const display = document.querySelector('.pd-price-display');
    if (!selected || !display) return;
    const priceText = selected.querySelector('.kaching-bundles__bar-price')?.textContent.trim();
    const compareText = selected.querySelector('.kaching-bundles__bar-full-price')?.textContent.trim();
    const current = display.querySelector('.pd-price-display__current');
    const compare = display.querySelector('.pd-price-display__compare');
    const savings = display.querySelector('.pd-price-display__savings');
    if (current && priceText) current.textContent = priceText;
    if (compare && compareText) compare.textContent = compareText;
    if (savings && priceText && compareText) {
      const price = parseFloat(priceText.replace(/[^\d.]/g, ''));
      const full = parseFloat(compareText.replace(/[^\d.]/g, ''));
      // The pricing_display block passes the theme setting "Product page savings badge text" as
      // data-template; Leventh's hero (snippets/leventh-pdp-hero) passes data-prefix ("Summer Sale | ").
      if (full > 0) {
        const pct = `${Math.round(((full - price) / full) * 100)}%`;
        const template = savings.dataset.template;
        savings.textContent = template
          ? template.split('[percentage]').join(pct)
          : `${savings.dataset.prefix || 'Summer Sale - '}${pct} off`;
      }
    }
  };
  document.addEventListener('change', (e) => {
    if (e.target.matches?.('.kaching-bundles__bars input[type="radio"]')) setTimeout(syncKachingPrice, 50);
  });
  document.addEventListener('click', (e) => {
    if (e.target.closest?.('.kaching-bundles__subscriptions')) setTimeout(syncKachingPrice, 50);
  });
  onReady(() => {
    if (document.querySelector('.pd-price-display')) setTimeout(syncKachingPrice, 300);
  });

  /* ---------- Loop Subscriptions widget -> theme prices / sale badge (live layout scripts) ---------- */
  const initLoopSync = () => {
    const root = document.querySelector('[data-br-loop-sync]');
    if (!root || !root.querySelector('.shopify-app-block')) return;
    let lastPrice = null;
    let lastBadge = null;
    const sync = () => {
      const selected = document.querySelector('.loop-widget-purchase-option-selected');
      if (!selected) return;
      const price = selected.querySelector('.loop-widget-purchase-option-price')?.textContent.trim();
      if (price && price !== lastPrice) {
        lastPrice = price;
        root.querySelectorAll('.price-item--sale.price-item--last, .price-item--regular.main-price').forEach((el) => {
          if (el.textContent.trim() !== price) el.textContent = price;
        });
      }
      const discount = (selected.querySelector('.loop-widget-purchase-option-discount-badge')?.textContent || '').match(/(\d+%)/);
      const badge = root.querySelector('.price__badge-sale .nowrap');
      if (discount && badge) {
        const text = `Summer Sale - ${discount[1]} OFF`;
        // Only write when the text really differs: every write is a childList mutation that re-triggers the observer.
        if (badge.textContent !== text) {
          lastBadge = text;
          badge.textContent = text;
        }
      }
    };
    new MutationObserver(() => {
      lastPrice = null;
      lastBadge = null;
      requestAnimationFrame(sync);
    }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    sync();
  };
  onReady(initLoopSync);

  /* ---------- Theme editor ---------- */
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      initGalleryDots(e.target);
      initDates(e.target);
    });
  }
})();
